/**
 * COD No-Store-Pickup SKU client helper (P0f, 2026-05-02)
 *
 * Single source of truth = server/data/no-store-pickup-skus.ts
 * Fetched via GET /api/cod/no-store-pickup-skus
 *
 * API:
 *   window.COD_NSP.fetchSkus()   -> Promise<Set<string> | null>
 *   window.COD_NSP.isBlocked(sku) -> boolean (sync, false if cache empty)
 *   window.COD_NSP.checkCart()    -> Promise<{hasBlocked, blockedItems, cart, fetchFailed}>
 *
 * Reliability:
 *   - 2 retry (1s, 2s exponential)
 *   - 5min in-memory cache
 *   - Single in-flight dedup
 *   - Fetch fail → fallback returns false (server enforces ground truth)
 */
(function () {
  'use strict';

  if (window.COD_NSP) {
    console.log('[COD_NSP] already initialized, skip');
    return;
  }

  var API_URL = 'https://neizhang.replit.app/api/cod/no-store-pickup-skus';
  var CACHE_TTL = 5 * 60 * 1000;
  var RETRY_DELAYS = [1000, 2000];

  var _cache = null;
  var _cacheTime = 0;
  var _fetchPromise = null;

  function sleep(ms) {
    return new Promise(function (r) { setTimeout(r, ms); });
  }

  async function fetchWithRetry(url, attemptIndex) {
    attemptIndex = attemptIndex || 0;
    try {
      var res = await fetch(url, { method: 'GET', credentials: 'omit' });
      if (!res.ok) throw new Error('fetch failed: ' + res.status);
      return await res.json();
    } catch (err) {
      if (attemptIndex < RETRY_DELAYS.length) {
        var delay = RETRY_DELAYS[attemptIndex];
        console.warn('[COD_NSP] fetch failed, retry in ' + delay + 'ms:', err);
        await sleep(delay);
        return fetchWithRetry(url, attemptIndex + 1);
      }
      throw err;
    }
  }

  function fetchSkus() {
    var now = Date.now();
    if (_cache && (now - _cacheTime < CACHE_TTL)) return Promise.resolve(_cache);
    if (_fetchPromise) return _fetchPromise;

    _fetchPromise = fetchWithRetry(API_URL)
      .then(function (data) {
        _cache = new Set(data.skus || []);
        _cacheTime = Date.now();
        _fetchPromise = null;
        console.log('[COD_NSP] loaded ' + _cache.size + ' SKUs');
        return _cache;
      })
      .catch(function (err) {
        _fetchPromise = null;
        console.error('[COD_NSP] fetch failed after retries:', err);
        return null;
      });

    return _fetchPromise;
  }

  function isBlocked(sku) {
    if (!_cache) return false;
    return _cache.has((sku || '').trim());
  }

  async function checkCart() {
    var skus = await fetchSkus();
    if (!skus) {
      return { hasBlocked: false, blockedItems: [], cart: null, fetchFailed: true };
    }
    try {
      var cartRes = await fetch('/cart.js', { cache: 'no-store' });
      var cart = await cartRes.json();
      var blocked = (cart.items || []).filter(function (item) {
        return skus.has((item.sku || '').trim());
      });
      return { hasBlocked: blocked.length > 0, blockedItems: blocked, cart: cart, fetchFailed: false };
    } catch (err) {
      console.warn('[COD_NSP] cart fetch failed:', err);
      return { hasBlocked: false, blockedItems: [], cart: null, fetchFailed: true };
    }
  }

  // Preload at script load time
  fetchSkus();

  window.COD_NSP = { fetchSkus: fetchSkus, isBlocked: isBlocked, checkCart: checkCart };
})();
